import React, { memo, useContext } from 'react';
import PropTypes, { oneOfType } from 'prop-types';
import { Link } from 'react-router-dom';
import format from 'date-fns/format';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import isValid from 'date-fns/isValid';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import ReactTooltip from 'react-tooltip';
import css from 'styled-jsx/css';

import ConditionsDropdown from './ConditionsDropdown';
import {
  ALERTS_CLEAR,
  ASSET_MODEL,
  ASSET_MODEL_READ,
  CORE,
  CORE_READ,
} from '../../Constants/RBAC';
import OrgsMapContext from '../../Context/OrgsMapContext';
import Can from '../../RBAC/Can';

const { className: tooltipClass, styles: tooltipStyles } = css.resolve`
  div {
    z-index: 1021;
  }
`;

const ALERT_STATUS_CLASS_MAP = new Map([
  ['positive', 'text-alert-orange'],
  ['negative', 'text-third-gray'],
]);

function truncateGps(number) {
  return Number.isInteger(number)
    ? number
    : Number.parseFloat(number).toFixed(2);
}

function AlertsTableBody({
  assetView,
  data,
  handleRuleLinkClick,
  selectedStatus,
  toggleClearAlertModalClick,
}) {
  const orgsMap = useContext(OrgsMapContext);

  return data.map((alert) => {
    const {
      AlertData: { signals } = {},
      Id,
      DetectedAt,
      GatewayId,
      GatewayName,
      GpsData: { Lat, Long },
      OrgId,
      RuleId,
      RuleName,
      Transition,
      TriggeredAt,
    } = alert;
    const showResolved = TriggeredAt && Transition === 'negative';
    const showClearButton =
      Transition === 'positive' && selectedStatus === 'positive';
    const detectedAtDate = new Date(DetectedAt);
    const detectedAtDisplay = isValid(detectedAtDate)
      ? format(detectedAtDate, 'MMM d Y - h:mmaaa')
      : null;
    const durationDisplay =
      isValid(detectedAtDate) && Transition === 'positive'
        ? `(${formatDistanceToNow(detectedAtDate, { includeSeconds: true })})`
        : null;

    return (
      <tr key={Id}>
        <td className="align-middle">
          <span
            className={`d-flex ${
              showClearButton ? 'justify-content-around' : ''
            } align-content-center`}
          >
            <FontAwesomeIcon
              icon={faCircle}
              size="sm"
              className={`${ALERT_STATUS_CLASS_MAP.get(Transition)} ${
                showClearButton ? '' : 'ml-3'
              } mt-1 mx-1`}
            />
            {showClearButton ? (
              <Can
                no={(data) => (
                  <div data-tip={data}>
                    <button
                      className="btn btn-sm btn-outline-secondary px-3 py-0 rounded mx-auto"
                      disabled
                    >
                      Clear
                    </button>
                    <ReactTooltip className={tooltipClass} />
                    {tooltipStyles}
                  </div>
                )}
                orgID={OrgId}
                perform={ALERTS_CLEAR}
                resource={CORE}
                yes={() => (
                  <button
                    className="btn btn-sm btn-outline-secondary px-3 py-0 rounded mx-auto"
                    onClick={() => toggleClearAlertModalClick(alert)}
                  >
                    Clear
                  </button>
                )}
              />
            ) : null}
          </span>
        </td>
        {assetView ? null : (
          <Can
            no={() => (
              <td className="align-middle">
                <button
                  className="btn btn-link p-0 three-quarter-text text-truncate"
                  disabled
                >
                  {GatewayName}
                </button>
              </td>
            )}
            orgID={OrgId}
            perform={CORE_READ}
            resource={CORE}
            yes={() => (
              <td className="align-middle">
                <Link
                  className="three-quarter-text text-truncate"
                  replace={true}
                  to={{
                    pathname: `/asset/${GatewayId}`,
                    search: '?tab=dashboard',
                  }}
                >
                  {GatewayName}
                </Link>
              </td>
            )}
          />
        )}
        <td className="align-middle">
          <Can
            no={() => (
              <button
                className="btn btn-link px-0 text-decoration-none three-quarter-text"
                disabled
                onClick={() => null}
              >
                <span className={ALERT_STATUS_CLASS_MAP.get(Transition)}>
                  {RuleName}
                </span>
              </button>
            )}
            orgID={OrgId}
            perform={ASSET_MODEL_READ}
            resource={ASSET_MODEL}
            yes={() => (
              <button
                className="btn btn-link px-0 text-decoration-none three-quarter-text"
                onClick={() => handleRuleLinkClick(GatewayId, RuleId)}
              >
                <span className={ALERT_STATUS_CLASS_MAP.get(Transition)}>
                  {RuleName}
                </span>
              </button>
            )}
          />
        </td>
        {assetView ? null : (
          <td className="align-middle three-quarter-text">
            {orgsMap?.[OrgId]?.name}
          </td>
        )}
        <td className="three-quarter-text align-middle">
          <p className="mb-0 text-nowrap">{detectedAtDisplay}</p>
          <div className="mb-0">{durationDisplay}</div>
        </td>
        {selectedStatus === 'positive' ? null : (
          <td className="three-quarter-text align-middle">
            {showResolved
              ? moment(TriggeredAt).format('MMM D Y - h:mma')
              : null}
          </td>
        )}
        <td className="pl-0">
          <ConditionsDropdown signals={signals} />
        </td>
        <td className="align-middle">
          {Lat && Long ? (
            <a
              className="three-quarter-text text-nowrap"
              href={`https://www.google.com/maps/dir/?api=1&destination=${Lat},${Long}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              {truncateGps(Lat)}, {truncateGps(Long)}
            </a>
          ) : (
            '--'
          )}
        </td>
      </tr>
    );
  });
}

AlertsTableBody.propTypes = {
  assetView: PropTypes.bool,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      AlertData: PropTypes.shape({
        description: oneOfType([
          PropTypes.string,
          PropTypes.arrayOf(PropTypes.string),
        ]),
        signals: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            signalID: PropTypes.string,
            threshold: oneOfType([PropTypes.number, PropTypes.string]),
            type: PropTypes.string,
            value: PropTypes.number,
          })
        ),
      }),
      CreatedAt: PropTypes.string,
      DetectedAt: PropTypes.string,
      GatewayId: PropTypes.string,
      GatewayName: PropTypes.string,
      GpsData: PropTypes.shape({
        Lat: PropTypes.number,
        Long: PropTypes.number,
      }),
      Id: PropTypes.string,
      OrgId: PropTypes.string,
      RuleId: PropTypes.string,
      RuleName: PropTypes.string,
      Severity: PropTypes.string,
      Transition: PropTypes.oneOf(['positive', 'negative']),
      TriggeredAt: PropTypes.string,
      Type: PropTypes.oneOf(['rule']),
      UpdatedAt: PropTypes.string,
    })
  ),
  handleRuleLinkClick: PropTypes.func,
  selectedStatus: PropTypes.string,
  toggleClearAlertModalClick: PropTypes.func,
};

AlertsTableBody.defaultProps = { data: [] };

export default memo(AlertsTableBody);
